import { createFileRoute, Link, Outlet, retainSearchParams, useNavigate, useLocation } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { LeagueNav } from "@/components/LeagueNav";
import { WatcherBanner } from "@/components/WatcherBanner";
import { useAuth } from "@/lib/auth-context";
import { LeagueProvider, type LeagueContextValue } from "@/lib/league-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { getAccessibleLeagueBySlug } from "@/lib/access.functions";
import { getMyLeagueProfile } from "@/lib/league-profile.functions";
import { listLeagueEntries } from "@/lib/entries.functions";
import {
  getPickableKnockoutCount,
  getPickableKnockoutCountsForViewer,
} from "@/lib/bracket.functions";
import { getLastEntryId } from "@/lib/last-entry";
import { toast } from "sonner";
import { ArrowLeft, Eye, Trophy, UserCog } from "lucide-react";

const leagueSearchSchema = z.object({
  /** When present, super admin is editing another member's picks. */
  actAs: fallback(z.string().uuid().optional(), undefined),
});

export const Route = createFileRoute("/_app/$league")({
  validateSearch: zodValidator(leagueSearchSchema),
  search: {
    middlewares: [retainSearchParams(["actAs"])],
  },
  component: LeagueLayout,
});

function LeagueLayout() {
  const { league } = Route.useParams();
  const { actAs } = Route.useSearch();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const navigate = useNavigate();
  const [leagueCtx, setLeagueCtx] = useState<LeagueContextValue | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const getLeagueFn = useAuthedServerFn(getAccessibleLeagueBySlug);
  const getProfileFn = useAuthedServerFn(getMyLeagueProfile);
  const listEntriesFn = useAuthedServerFn(listLeagueEntries);
  const getPickableFn = useAuthedServerFn(getPickableKnockoutCount);
  const getPickableAllFn = useAuthedServerFn(getPickableKnockoutCountsForViewer);
  // Stable refs so refreshViewerHasEntry can always see the latest values
  // without forcing the callback identity (and the LeagueProvider value) to change.
  const leagueIdRef = useRef<string | null>(null);
  const subjectIdRef = useRef<string | null>(null);
  const activeEntryIdRef = useRef<string | null>(null);

  const refreshPickableKnockout = useCallback(async () => {
    const leagueId = leagueIdRef.current;
    const entryId = activeEntryIdRef.current;
    if (!leagueId) return;
    try {
      const targetUserId =
        subjectIdRef.current && subjectIdRef.current !== userId
          ? subjectIdRef.current
          : undefined;
      // Fetch active-entry count + all-entry breakdown in parallel.
      const [single, all] = await Promise.all([
        entryId
          ? getPickableFn({ data: { leagueId, entryId, targetUserId } })
          : Promise.resolve({ count: 0, matchIds: [] as string[] }),
        getPickableAllFn({ data: { leagueId, targetUserId } }),
      ]);
      setLeagueCtx((prev) =>
        prev && prev.id === leagueId
          ? {
              ...prev,
              pickableKnockoutCount: single.count,
              pickableKnockoutMatchIds: single.matchIds,
              pickableKnockoutByEntry: all.entries,
            }
          : prev,
      );
    } catch {
      // Non-fatal — leave existing count in place.
    }
  }, [getPickableFn, getPickableAllFn, userId]);

  const refreshViewerHasEntry = useCallback(async () => {
    const leagueId = leagueIdRef.current;
    const subjectId = subjectIdRef.current;
    if (!leagueId || !subjectId) return;
    try {
      const entries = await listEntriesFn({ data: { leagueId } });
      const has = entries.some((e) => e.ownerUserId === subjectId);
      setLeagueCtx((prev) =>
        prev && prev.id === leagueId ? { ...prev, viewerHasEntry: has } : prev,
      );
      // Active entry may have appeared/disappeared — recompute pickable count.
      const mine = entries.filter((e) => e.ownerUserId === subjectId);
      const last = getLastEntryId(leagueId);
      const next = mine.find((e) => e.id === last)?.id ?? mine[0]?.id ?? null;
      activeEntryIdRef.current = next;
      if (next) {
        void refreshPickableKnockout();
      } else {
        setLeagueCtx((prev) =>
          prev && prev.id === leagueId
            ? {
                ...prev,
                pickableKnockoutCount: 0,
                pickableKnockoutMatchIds: [],
                pickableKnockoutByEntry: [],
              }
            : prev,
        );
      }
    } catch {
      // Swallow — stale value is preferable to flipping the gate on a transient failure.
    }
  }, [listEntriesFn, refreshPickableKnockout]);

  // Only super admins can use override mode. If a non-admin somehow lands
  // on a URL with ?actAs=, ignore it.
  const effectiveActAs = isAdmin && actAs && actAs !== userId ? actAs : null;

  const currentKey = userId
    ? `${userId}::${league}::${effectiveActAs ?? ""}`
    : null;
  const hasFreshData = leagueCtx !== null && loadedKey === currentKey;

  useEffect(() => {
    if (authLoading) return;
    if (!userId) return;

    let cancelled = false;

    (async () => {
      try {
        const lg = await getLeagueFn({ data: { slug: league } });
        if (cancelled) return;

        let actingAs: LeagueContextValue["actingAs"] = null;
        if (effectiveActAs) {
          try {
            const targetProfile = await getProfileFn({
              data: { leagueId: lg.id, targetUserId: effectiveActAs },
            });
            const label =
              targetProfile?.displayName ||
              targetProfile?.realName ||
              "this member";
            actingAs = { userId: effectiveActAs, label };
          } catch {
            actingAs = { userId: effectiveActAs, label: "this member" };
          }
        }

        if (cancelled) return;

        // Determine whether the viewer (or override target) has at least
        // one active entry. Used to gate Groups/Knockout tabs.
        let viewerHasEntry = false;
        let activeEntryId: string | null = null;
        const subjectId = effectiveActAs ?? userId;
        try {
          const entries = await listEntriesFn({ data: { leagueId: lg.id } });
          const mine = entries.filter((e) => e.ownerUserId === subjectId);
          viewerHasEntry = mine.length > 0;
          // Pick the active entry the same way useActiveEntry does: last-viewed
          // (self mode), else oldest (entries arrive sorted asc by created_at).
          if (mine.length > 0) {
            const last = effectiveActAs ? null : getLastEntryId(lg.id);
            activeEntryId = mine.find((e) => e.id === last)?.id ?? mine[0].id;
          }
        } catch {
          // Fall back to hiding tabs on failure — safer than showing them.
          viewerHasEntry = false;
        }
        if (cancelled) return;

        // Fetch pickable knockout counts (G+K only): active-entry count for
        // the Knockout tab dot, plus per-entry breakdown for the aggregate
        // banner and MyEntriesPanel pills.
        let pickableCount = 0;
        let pickableIds: string[] = [];
        let pickableByEntry: LeagueContextValue["pickableKnockoutByEntry"] = [];
        const isGK = lg.tournament_type === "fifa_wc_mens_group_knockout";
        if (viewerHasEntry) {
          try {
            const [single, all] = await Promise.all([
              isGK && activeEntryId
                ? getPickableFn({
                    data: {
                      leagueId: lg.id,
                      entryId: activeEntryId,
                      targetUserId: effectiveActAs ?? undefined,
                    },
                  })
                : Promise.resolve({ count: 0, matchIds: [] as string[] }),
              getPickableAllFn({
                data: {
                  leagueId: lg.id,
                  targetUserId: effectiveActAs ?? undefined,
                },
              }),
            ]);
            pickableCount = single.count;
            pickableIds = single.matchIds;
            pickableByEntry = all.entries;
          } catch {
            // Non-fatal — leave at 0.
          }
        }
        if (cancelled) return;

        leagueIdRef.current = lg.id;
        subjectIdRef.current = effectiveActAs ?? userId;
        activeEntryIdRef.current = activeEntryId;

        setLeagueCtx({
          id: lg.id,
          slug: lg.slug,
          name: lg.name,
          bracketLockAt: lg.bracket_lock_at,
          bracketLockTimezone: lg.bracket_lock_timezone ?? "UTC",
          tournamentType: lg.tournament_type,
          groupStageLockAt: lg.group_stage_lock_at,
          knockoutPickable:
            lg.tournament_type === "fifa_wc_mens_group_knockout"
              ? !!lg.knockout_pickable
              : true,
          maxEntriesPerMember: lg.max_entries_per_member ?? 1,
          memberRole: lg.member_role === "watcher" ? "watcher" : "participant",
          viewerHasEntry,
          actingAs,
          refreshViewerHasEntry,
          pickableKnockoutCount: pickableCount,
          pickableKnockoutMatchIds: pickableIds,
          pickableKnockoutByEntry: pickableByEntry,
          refreshPickableKnockout,
        });
        setLoadedKey(`${userId}::${league}::${effectiveActAs ?? ""}`);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : "League not found";
        setLeagueCtx(null);
        setLoadedKey(null);
        toast.error(message);
        navigate({ to: isAdmin ? "/admin" : "/home" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    league,
    userId,
    isAdmin,
    authLoading,
    navigate,
    getLeagueFn,
    getProfileFn,
    listEntriesFn,
    getPickableFn,
    getPickableAllFn,
    effectiveActAs,
    refreshViewerHasEntry,
    refreshPickableKnockout,
  ]);

  // Re-validate access on focus + every 30s. If the user has been removed
  // from the league mid-session, bounce them to /home with a toast instead
  // of letting them linger on broken sub-pages (bracket, groups, etc.).
  useEffect(() => {
    if (authLoading || !userId) return;
    let cancelled = false;
    const check = async () => {
      try {
        await getLeagueFn({ data: { slug: league } });
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : "Access to this league was removed.";
        toast.error(message);
        navigate({ to: isAdmin ? "/admin" : "/home" });
      }
    };
    const onFocus = () => {
      void check();
      void refreshViewerHasEntry();
      void refreshPickableKnockout();
    };
    window.addEventListener("focus", onFocus);
    const id = window.setInterval(check, 30_000);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(id);
    };
  }, [league, userId, isAdmin, authLoading, navigate, getLeagueFn, refreshViewerHasEntry, refreshPickableKnockout]);

  // Per-page browser tab title: "<Section> — <League name>"
  const location = useLocation();
  useEffect(() => {
    const name = leagueCtx?.name;
    if (!name) return;
    const path = location.pathname;
    let section = "Leaderboard";
    if (path.includes("/groups")) section = "Group picks";
    else if (path.includes("/bracket")) section = "Knockout picks";
    else if (path.includes("/entries/new")) section = "New bracket";
    else if (path.includes("/entries/")) section = "Edit bracket";
    const prev = document.title;
    document.title = `${section} — ${name}`;
    return () => {
      document.title = prev;
    };
  }, [location.pathname, leagueCtx?.name]);

  if (!hasFreshData && !leagueCtx) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Trophy className="h-5 w-5 animate-pulse text-primary" />
          <span className="text-sm">Loading league…</span>
        </div>
      </div>
    );
  }

  if (!leagueCtx) return null;

  const inOverride = leagueCtx.actingAs !== null;

  return (
    <LeagueProvider value={leagueCtx}>
      <div className="min-h-screen bg-background">
        {inOverride && (
          // Sticky, persistent override banner — visible across every league sub-route
          <div className="sticky top-0 z-40 border-b-2 border-amber-500 bg-amber-500/95 text-amber-950 shadow-md">
            <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs sm:text-sm">
              <div className="flex items-center gap-2 font-semibold">
                <UserCog className="h-4 w-4 shrink-0" />
                <span>
                  OVERRIDE MODE — editing as{" "}
                  <span className="font-bold underline">
                    {leagueCtx.actingAs?.label}
                  </span>{" "}
                  in {leagueCtx.name}
                </span>
              </div>
              <Link
                to="/admin/leagues/$leagueId"
                params={{ leagueId: leagueCtx.id }}
                className="inline-flex items-center gap-1 rounded-md border-2 border-amber-950/40 bg-amber-50/80 px-2 py-1 font-bold text-amber-950 transition-colors hover:bg-amber-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Exit override
              </Link>
            </div>
          </div>
        )}
        {isAdmin && !inOverride && (
          <div className="border-b border-amber-500/30 bg-amber-500/10">
            <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs sm:text-sm">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Eye className="h-4 w-4 shrink-0" />
                <span>
                  Viewing <span className="font-semibold">{leagueCtx.name}</span> as a member
                </span>
              </div>
              <Link
                to="/admin/leagues/$leagueId"
                params={{ leagueId: leagueCtx.id }}
                className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-background/60 px-2 py-1 font-medium text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-300"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Exit member view
              </Link>
            </div>
          </div>
        )}
        {!location.pathname.includes("/members/") && <LeagueNav />}
        {!location.pathname.includes("/members/") && <WatcherBanner />}
        <Outlet />
      </div>
    </LeagueProvider>
  );
}
